/**
 * ErrorBoundary globale: catch errori in render e mostra UI di fallback
 * invece di schermo bianco.
 *
 * Bug tipico catturato: setState con oggetto/array passato come children
 * di un <div>{x}</div> dove React si aspetta string/number/ReactNode.
 */

import { Component, type ErrorInfo, type ReactNode } from "react";
import { tr } from "../../i18n";

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  override componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    // eslint-disable-next-line no-console
    console.error("ErrorBoundary caught:", error, errorInfo);
    this.setState({ errorInfo });
  }

  handleReload = (): void => {
    this.setState({ hasError: false, error: null, errorInfo: null });
    window.location.reload();
  };

  handleGoHome = (): void => {
    this.setState({ hasError: false, error: null, errorInfo: null });
    window.location.href = "/";
  };

  override render() {
    if (this.state.hasError) {
      const errMsg = this.state.error?.message ?? tr("errore sconosciuto");
      const stack = this.state.errorInfo?.componentStack ?? "";
      return (
        <div className="min-h-screen flex items-center justify-center p-4 bg-bg">
          <div className="card max-w-lg w-full border border-danger/40 space-y-3">
            <h2 className="font-display text-2xl text-danger">{tr("⚠ Errore inatteso")}</h2>
            <p className="text-sm text-fg">
              {tr(
                tr(
                  "La pagina ha incontrato un errore e si è bloccata. Ricarica per riprendere — i dati non sono persi.",
                ),
              )}
            </p>
            <div className="bg-primary-deep/40 rounded p-2 text-xs font-mono text-danger break-words">
              {errMsg}
            </div>
            {stack && (
              <details className="text-xs text-fg-muted">
                <summary className="cursor-pointer">Stack trace</summary>
                <pre className="mt-1 whitespace-pre-wrap text-[10px]">{stack}</pre>
              </details>
            )}
            <div className="flex gap-2">
              <button type="button" onClick={this.handleReload} className="btn-primary text-sm">
                {tr("🔄 Ricarica pagina")}
              </button>
              <button type="button" onClick={this.handleGoHome} className="btn-ghost text-sm">
                {tr("🏠 Torna alla home")}
              </button>
            </div>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
